export interface Job {
  id: string;
  title: string;
  location: string;
  payRate: string;
  contractType: "Temporary" | "Permanent" | "Contract";
  category: string;
  schedule?: string;
  duration?: string;
  dbsRequired?: string;
  datePosted?: string; // ISO date string (YYYY-MM-DD) - when the advert went live
  closingDate?: string; // ISO date string (YYYY-MM-DD)
  description: string;
  responsibilities?: string[];
  qualifications?: string[];
  uniform?: string[];
  healthSafety?: string[];
  competencies?: string[];
  featured?: boolean;
  pinned?: boolean; // Always shown at the very top of the board, above urgent roles
  urgent?: boolean;
  removeAfter?: string; // Last calendar date the advert remains publicly visible (YYYY-MM-DD)
  openings?: number; // Number of vacancies, used in JobPosting structured data when confirmed
  postcodes?: string[]; // Outward codes (e.g., DY8, B17, CV3)
}

// Postcode to location mapping for search functionality
export const postcodeLocationMap: Record<string, string[]> = {
  // Bristol
  "BS7": ["Horfield", "Bristol"],

  // West Midlands - Birmingham area
  "B17": ["Harborne", "Birmingham"],
  "B66": ["Smethwick"],
  "B67": ["Smethwick"],
  "B68": ["Oldbury"],
  "B69": ["Oldbury"],
  
  // West Midlands - Dudley/Black Country
  "DY1": ["Dudley"],
  "DY2": ["Dudley"],
  "DY3": ["Dudley"],
  "DY4": ["Dudley", "Tipton"],
  "DY5": ["Brierley Hill"],
  "DY8": ["Stourbridge", "Hagley"],
  
  // Worcestershire
  "DY10": ["Kidderminster"],
  "DY11": ["Kidderminster"],
  "DY13": ["Stourport-on-Severn", "Stourport-On-Severn"],

  // Shropshire
  "TF11": ["Shifnal"],
  
  // Coventry
  "CV3": ["Coventry"],
  "CV6": ["Coventry"],
  
  // Kent - Canterbury
  "CT1": ["Canterbury", "Kent"],
  "CT2": ["Canterbury", "Kent"],
  "CT3": ["Canterbury", "Kent"],
  "CT4": ["Canterbury", "Kent"],
  // Kent - Medway
  "ME4": ["Chatham", "Medway", "Kent"],
  "ME16": ["Maidstone", "Kent"],
  // Kent - Tonbridge & Tunbridge Wells
  "TN1": ["Tunbridge Wells", "Kent"],
  "TN2": ["Tunbridge Wells", "Kent"],
  "TN3": ["Tunbridge Wells", "Kent"],
  "TN4": ["Tunbridge Wells", "Kent"],
  "TN9": ["Tonbridge", "Kent"],
  "TN10": ["Tonbridge", "Kent"],
  "TN11": ["Tonbridge", "Kent"],
  "TN12": ["Tonbridge", "Kent"],
  // East Sussex - Hastings / St Leonards
  "TN34": ["Hastings", "East Sussex"],
  "TN35": ["Hastings", "East Sussex"],
  "TN37": ["St Leonards-on-Sea", "Hastings", "East Sussex"],
  "TN38": ["St Leonards-on-Sea", "Hastings", "East Sussex"],
  "TN39": ["Bexhill-on-Sea", "East Sussex"],
  "TN40": ["Bexhill-on-Sea", "East Sussex"],

  // Cornwall
  "TR14": ["Camborne", "Cornwall"],
  "TR15": ["Redruth", "Cornwall"],
  "TR16": ["Redruth", "Cornwall"],
  "TR1": ["Truro", "Cornwall"],
  "TR2": ["Truro", "Cornwall"],
  "TR3": ["Truro", "Cornwall"],

  // Somerset
  "TA1": ["Taunton", "Somerset"],
  "TA2": ["Taunton", "Somerset"],
  "TA6": ["Bridgwater", "Somerset"],
  "TA7": ["Bridgwater", "Somerset"],


  // London - South East (all SE postcodes)
  "SE1": ["Southwark", "London Bridge", "Bermondsey", "London"],
  "SE2": ["Abbey Wood", "Bexley", "London"],
  "SE3": ["Blackheath", "Greenwich", "London"],
  "SE4": ["Brockley", "Lewisham", "London"],
  "SE5": ["Camberwell", "Southwark", "London"],
  "SE6": ["Catford", "Lewisham", "London"],
  "SE7": ["Charlton", "Greenwich", "London"],
  "SE8": ["Deptford", "Lewisham", "London"],
  "SE9": ["Eltham", "Mottingham", "Greenwich", "London"],
  "SE10": ["Greenwich", "London"],
  "SE11": ["Kennington", "Lambeth", "London"],
  "SE12": ["Lee", "Grove Park", "Lewisham", "London"],
  "SE13": ["Lewisham", "Hither Green", "London"],
  "SE14": ["New Cross", "Lewisham", "London"],
  "SE15": ["Peckham", "Nunhead", "Southwark", "London"],
  "SE16": ["Rotherhithe", "Bermondsey", "Southwark", "London"],
  "SE17": ["Walworth", "Southwark", "London"],
  "SE18": ["Woolwich", "Plumstead", "Greenwich", "London"],
  "SE19": ["Upper Norwood", "Crystal Palace", "London"],
  "SE20": ["Penge", "Anerley", "Bromley", "London"],
  "SE21": ["Dulwich", "Tulse Hill", "Southwark", "London"],
  "SE22": ["East Dulwich", "Southwark", "London"],
  "SE23": ["Forest Hill", "Lewisham", "London"],
  "SE24": ["Herne Hill", "Tulse Hill", "Lambeth", "London"],
  "SE25": ["South Norwood", "Croydon", "London"],
  "SE26": ["Sydenham", "Lewisham", "London"],
  "SE27": ["West Norwood", "Lambeth", "London"],
  "SE28": ["Thamesmead", "Greenwich", "London"],

  // London - Angel (EC1V) and Croydon (CR0)
  "EC1V": ["Angel", "Clerkenwell", "Finsbury", "London"],
  "CR0": ["Croydon", "London"],


  // London - South West (partial)
  "SW2": ["Brixton", "Lambeth", "London"],
  "SW4": ["Clapham", "Lambeth", "London"],
  "SW8": ["South Lambeth", "Lambeth", "London"],
  "SW9": ["Stockwell", "Lambeth", "London"],

  // London - Bromley (BR)
  "BR1": ["Bromley", "London"],
  "BR2": ["Bromley", "Hayes", "London"],
  "BR3": ["Beckenham", "Bromley", "London"],
  "BR4": ["West Wickham", "Bromley", "London"],
  "BR5": ["Orpington", "Bromley", "London"],
  "BR6": ["Orpington", "Bromley", "London"],
  "BR7": ["Chislehurst", "Bromley", "London"],
  "BR8": ["Swanley", "Bromley", "London"],


  // London - Ealing (West London)
  "W5": ["Ealing", "West London"],
  "W13": ["Ealing", "West London"],
  "UB1": ["Southall", "Ealing", "West London"],
  "UB2": ["Southall", "Ealing", "West London"],
  "UB5": ["Northolt", "Ealing", "West London"],
  "UB6": ["Greenford", "Ealing", "West London"],

  // Herefordshire - Kington
  "HR5": ["Kington", "Herefordshire"],
  "HR1": ["Hereford", "Herefordshire"],
  "HR2": ["Hereford", "Herefordshire"],
  "HR3": ["Hereford", "Herefordshire"],
  "HR4": ["Hereford", "Herefordshire"],

  // Worcestershire - Worcester
  "WR1": ["Worcester", "Worcestershire"],
  "WR2": ["Worcester", "Worcestershire"],
  "WR3": ["Worcester", "Worcestershire"],
  "WR4": ["Worcester", "Worcestershire"],
  "WR5": ["Worcester", "Worcestershire"],

  // Hertfordshire
  "EN7": ["Cheshunt", "Hertfordshire"],
  "EN8": ["Cheshunt", "Hertfordshire"],
  "EN10": ["Hertfordshire", "Broxbourne"],
  "EN11": ["Hertfordshire", "Hoddesdon"],
  
  // Sussex - Brighton
  "BN1": ["Brighton", "East Sussex"],
  "BN2": ["Brighton", "East Sussex"],
  "BN3": ["Hove", "Brighton", "East Sussex"],

  // Dorset - Bournemouth & Poole
  "BH1": ["Bournemouth", "Dorset"],
  "BH2": ["Bournemouth", "Dorset"],
  "BH3": ["Bournemouth", "Dorset"],
  "BH4": ["Bournemouth", "Westbourne", "Dorset"],
  "BH5": ["Boscombe", "Bournemouth", "Dorset"],
  "BH6": ["Southbourne", "Bournemouth", "Dorset"],
  "BH7": ["Bournemouth", "Dorset"],
  "BH8": ["Bournemouth", "Dorset"],
  "BH9": ["Bournemouth", "Dorset"],
  "BH10": ["Bournemouth", "Dorset"],
  "BH11": ["Bournemouth", "Dorset"],

  "DT1": ["Dorchester", "Dorset"],
  "DT2": ["Dorchester", "Dorset"],
  "DT6": ["Bridport", "Dorset"],
  "DT10": ["Sturminster Newton", "Dorset"],
  "DT11": ["Blandford Forum", "Dorset"],

  // Suffolk
  "IP1": ["Ipswich", "Suffolk"],
  "IP2": ["Ipswich", "Suffolk"],
  "IP3": ["Ipswich", "Suffolk"],
  "IP4": ["Ipswich", "Suffolk"],
  "IP5": ["Ipswich", "Kesgrave", "Suffolk"],
  "IP6": ["Ipswich", "Suffolk"],
  "IP7": ["Hadleigh", "Suffolk"],
  "IP8": ["Ipswich", "Suffolk"],
  "IP9": ["Ipswich", "Suffolk"],
  "IP10": ["Ipswich", "Suffolk"],
  "IP11": ["Felixstowe", "Suffolk"],
  "IP12": ["Woodbridge", "Suffolk"],
  "IP13": ["Woodbridge", "Suffolk"],
  "IP14": ["Stowmarket", "Suffolk"],
  "IP15": ["Aldeburgh", "Suffolk"],
  "IP16": ["Leiston", "Suffolk"],
  "IP17": ["Saxmundham", "Suffolk"],
  "IP18": ["Southwold", "Suffolk"],
  "IP19": ["Halesworth", "Suffolk"],
  "IP20": ["Harleston", "Suffolk"],
  "IP21": ["Eye", "Suffolk"],
  "IP22": ["Diss", "Suffolk"],
  "IP23": ["Eye", "Suffolk"],
  "IP27": ["Brandon", "Suffolk"],
  "IP28": ["Bury St Edmunds", "Suffolk"],
  "IP29": ["Bury St Edmunds", "Suffolk"],
  "IP30": ["Bury St Edmunds", "Suffolk"],
  "IP31": ["Bury St Edmunds", "Suffolk"],
  "IP32": ["Bury St Edmunds", "Suffolk"],
  "IP33": ["Bury St Edmunds", "Suffolk"],

  // Wiltshire - Salisbury
  "SP1": ["Salisbury", "Wiltshire"],

  // Essex - Basildon
  "SS15": ["Basildon", "Essex"],


  // Yorkshire - Doncaster
  "DN1": ["Doncaster"],
  "DN2": ["Doncaster"],
  "DN3": ["Doncaster"],
  "DN4": ["Doncaster"],
  "DN5": ["Doncaster"],
  "DN6": ["Doncaster"],
  "DN7": ["Doncaster"],
  "DN8": ["Doncaster"],
  "DN9": ["Doncaster"],
  "DN10": ["Doncaster"],
  "DN11": ["Doncaster"],
  "DN12": ["Doncaster"],

  // Yorkshire - Leeds
  "LS1": ["Leeds"],
  "LS2": ["Leeds"],
  "LS3": ["Leeds"],
  "LS4": ["Leeds"],
  "LS5": ["Leeds"],
  "LS6": ["Leeds"],
  "LS7": ["Leeds"],
  "LS8": ["Leeds"],
  "LS9": ["Leeds"],
  "LS10": ["Leeds"],
  "LS11": ["Leeds"],
  "LS12": ["Leeds"],
  "LS13": ["Leeds"],
  "LS14": ["Leeds"],
  "LS15": ["Leeds"],
  "LS16": ["Leeds"],
  "LS17": ["Leeds"],
  "LS18": ["Leeds"],
  "LS19": ["Leeds"],
  "LS20": ["Leeds"],
  "LS21": ["Leeds"],
  "LS22": ["Leeds"],
  "LS23": ["Leeds"],
  "LS24": ["Leeds"],
  "LS25": ["Leeds"],
  "LS26": ["Leeds"],
  "LS27": ["Leeds"],
  "LS28": ["Leeds"],

  // Yorkshire - Sheffield
  "S1": ["Sheffield"],
  "S2": ["Sheffield"],
  "S3": ["Sheffield"],
  "S4": ["Sheffield"],
  "S5": ["Sheffield"],
  "S6": ["Sheffield"],
  "S7": ["Sheffield"],
  "S8": ["Sheffield"],
  "S9": ["Sheffield"],
  "S10": ["Sheffield"],
  "S11": ["Sheffield"],
  "S12": ["Sheffield"],
  "S13": ["Sheffield"],
  "S14": ["Sheffield"],

  // Yorkshire - York
  "YO1": ["York"],
  "YO10": ["York"],
  "YO19": ["York"],
  "YO23": ["York"],
  "YO24": ["York"],
  "YO26": ["York"],
  "YO30": ["York"],
  "YO31": ["York"],
  "YO32": ["York"],

  // Yorkshire - Bradford
  "BD1": ["Bradford"],
  "BD2": ["Bradford"],
  "BD3": ["Bradford"],
  "BD4": ["Bradford"],
  "BD5": ["Bradford"],
  "BD6": ["Bradford"],
  "BD7": ["Bradford"],
  "BD8": ["Bradford"],
  "BD9": ["Bradford"],
  "BD10": ["Bradford"],
  "BD11": ["Bradford"],
  "BD12": ["Bradford"],
  "BD13": ["Bradford"],
  "BD14": ["Bradford"],
  "BD15": ["Bradford"],
  "BD16": ["Bradford"],
  "BD17": ["Bradford"],
  "BD18": ["Bradford"],
  "BD19": ["Bradford"],
  "BD20": ["Bradford"],
  "BD21": ["Bradford"],
  "BD22": ["Bradford"],
  "BD23": ["Bradford"],

  // Yorkshire - Harrogate
  "HG1": ["Harrogate"],
  "HG2": ["Harrogate"],
  "HG3": ["Harrogate"],
  "HG4": ["Harrogate"],
  "HG5": ["Harrogate"],

  // Yorkshire - Halifax
  "HX1": ["Halifax"],
  "HX2": ["Halifax"],
  "HX3": ["Halifax"],
  "HX4": ["Halifax"],
  "HX5": ["Halifax"],
  "HX6": ["Halifax"],
  "HX7": ["Halifax"],

  // Yorkshire - Wakefield
  "WF1": ["Wakefield"],
  "WF2": ["Wakefield"],
  "WF3": ["Wakefield"],
  "WF4": ["Wakefield"],
  "WF5": ["Wakefield"],
  "WF6": ["Wakefield"],
  "WF7": ["Wakefield"],
  "WF8": ["Wakefield"],
  "WF9": ["Wakefield"],
  "WF10": ["Wakefield"],
  "WF11": ["Wakefield"],
  "WF12": ["Wakefield"],
  "WF13": ["Wakefield"],
  "WF14": ["Wakefield"],
  "WF15": ["Wakefield"],
  "WF16": ["Wakefield"],
  "WF17": ["Wakefield"],

  // Yorkshire - Huddersfield
  "HD1": ["Huddersfield"],
  "HD2": ["Huddersfield"],
  "HD3": ["Huddersfield"],
  "HD4": ["Huddersfield"],
  "HD5": ["Huddersfield"],
  "HD6": ["Huddersfield"],
  "HD7": ["Huddersfield"],
  "HD8": ["Huddersfield"],
  "HD9": ["Huddersfield"],
};

export const jobs: Job[] = [
  {
    id: "cleaner-ipswich-enhanced",
    title: "Cleaner (Enhanced DBS)",
    location: "Ipswich, Suffolk",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "9:00 AM - 1:00 PM BST",
    duration: "Until 11 July 2026, then ongoing",
    dbsRequired: "Enhanced DBS",
    closingDate: "2026-07-11",
    postcodes: ["IP4"],
    description: "To deliver high-quality enhanced cleaning services in Ipswich, maintaining high hygiene standards particularly across high-touch areas, ensuring the site remains clean, safe, and compliant.",
    responsibilities: [
      "Enhanced cleaning duties across designated areas.",
      "Touch-point cleaning to maintain strict hygiene standards.",
      "General cleaning of allocated areas.",
      "Ensuring all tasks are completed within allocated timeframes.",
      "Working professionally within a sensitive environment.",
      "Reporting any issues, hazards, or maintenance concerns."
    ],
    qualifications: [
      "Valid Enhanced DBS Certificate (Compulsory)",
      "Previous cleaning experience is desirable but not essential",
      "Ability to follow site instructions and health & safety procedures",
      "Reliable, punctual, and professional"
    ],
    uniform: [
      "Black button-up shirt (ironed)",
      "Black tie",
      "Long smart black suit trousers (must reach shoes)",
      "Black leather shoes (no trainers, boots, loafers, heels, or pumps)",
      "Black long socks",
      "Fingernails must be short",
      "No nail varnish or facial piercings"
    ],
    healthSafety: [
      "Follow all site safety procedures and guidance.",
      "Safely handle cleaning materials and chemicals as instructed.",
      "Report accidents, hazards, or near misses immediately.",
      "Maintain a safe working environment."
    ],
    competencies: [
      "Reliability and punctuality",
      "Attention to detail",
      "Ability to work independently",
      "Professional conduct",
      "Good communication skills"
    ],
    featured: false,
    urgent: true,
  },
  {
    id: "cleaner-maidstone-may-june-a",
    title: "Cleaner (Basic DBS)",
    location: "Maidstone, Kent, ME16",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Monday to Friday, 7:00 AM – 11:00 AM",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    closingDate: "2026-12-31",
    postcodes: ["ME16"],
    description: "To provide cleaning cover in Maidstone (ME16), with early-morning weekday shifts cleaning communal and operational areas to a high standard.",
    responsibilities: [
      "Carry out daily cleaning across communal and operational areas.",
      "Touch-point cleaning of high-contact surfaces.",
      "Empty bins, replenish supplies, and maintain hygiene standards.",
      "Follow site-specific safety and security procedures.",
      "Report maintenance or safety issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (must be presented on site)",
      "Valid photo ID and right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all Health & Safety and COSHH procedures.",
      "Use cleaning products and equipment safely.",
      "Maintain high standards of hygiene to limit infection spread."
    ],
    competencies: [
      "Reliability and punctuality",
      "Strong attention to detail",
      "Ability to work independently",
      "Professionalism in customer-facing environments"
    ],
    featured: true,
    urgent: false,
  },
  {
    id: "cleaner-maidstone-may-june-b",
    title: "Cleaner (Basic DBS)",
    location: "Maidstone, Kent, ME16",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Monday to Friday, 7:00 AM – 11:00 AM",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    closingDate: "2026-12-31",
    postcodes: ["ME16"],
    description: "To provide cleaning cover in Maidstone (ME16), with early-morning weekday shifts cleaning communal and operational areas to a high standard.",
    responsibilities: [
      "Carry out daily cleaning across communal and operational areas.",
      "Touch-point cleaning of high-contact surfaces.",
      "Empty bins, replenish supplies, and maintain hygiene standards.",
      "Follow site-specific safety and security procedures.",
      "Report maintenance or safety issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (must be presented on site)",
      "Valid photo ID and right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all Health & Safety and COSHH procedures.",
      "Use cleaning products and equipment safely.",
      "Maintain high standards of hygiene to limit infection spread."
    ],
    competencies: [
      "Reliability and punctuality",
      "Strong attention to detail",
      "Ability to work independently",
      "Professionalism in customer-facing environments"
    ],
    featured: true,
    urgent: false,
  },
  {
    id: "cleaner-camborne-tr14-urgent",
    title: "Cleaner (Basic DBS)",
    location: "Camborne, Cornwall, TR14",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "12:00 PM – 2:00 PM",
    duration: "Ongoing, immediate start",
    dbsRequired: "Basic DBS",
    closingDate: "2026-12-31",
    postcodes: ["TR14"],
    description: "To provide cleaning cover in Camborne, Cornwall (TR14), with afternoon weekday shifts cleaning communal and operational areas to a high standard.",
    responsibilities: [
      "Carry out daily cleaning across communal and operational areas.",
      "Touch-point cleaning of high-contact surfaces.",
      "Empty bins, replenish supplies, and maintain hygiene standards.",
      "Follow site-specific safety and security procedures.",
      "Report maintenance or safety issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (must be presented on site)",
      "Valid photo ID and right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all Health & Safety and COSHH procedures.",
      "Use cleaning products and equipment safely.",
      "Maintain high standards of hygiene to limit infection spread."
    ],
    competencies: [
      "Reliability and punctuality",
      "Strong attention to detail",
      "Ability to work independently",
      "Professionalism in customer-facing environments"
    ],
    featured: true,
    urgent: false,
  },
  {
    id: "cleaner-chatham-me4-may-june",
    title: "Cleaner (Basic DBS)",
    location: "Chatham, Kent, ME4",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Monday to Friday, 6:30 AM – 9:00 AM",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    closingDate: "2026-12-31",
    postcodes: ["ME4"],
    description: "To provide early-morning cleaning cover in Chatham (ME4), with weekday shifts cleaning communal and operational areas to a high standard.",
    responsibilities: [
      "Carry out daily cleaning across communal and operational areas.",
      "Touch-point cleaning of high-contact surfaces.",
      "Empty bins, replenish supplies, and maintain hygiene standards.",
      "Follow site-specific safety and security procedures.",
      "Report maintenance or safety issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (must be presented on site)",
      "Valid photo ID and right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all Health & Safety and COSHH procedures.",
      "Use cleaning products and equipment safely.",
      "Maintain high standards of hygiene to limit infection spread."
    ],
    competencies: [
      "Reliability and punctuality",
      "Strong attention to detail",
      "Ability to work independently",
      "Professionalism in customer-facing environments"
    ],
    featured: true,
    urgent: false,
  },
  {
    id: "enhanced-cleaner-dudley",
    title: "Cleaner (Enhanced DBS)",
    location: "Dudley, West Midlands",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (less than 30 hours per week)",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS",
    postcodes: ["DY1", "DY2", "DY3", "DY4"],
    description: "To deliver high-quality enhanced cleaning services, ensuring all communal and touch-point areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out enhanced touch-point cleaning across all communal areas.",
      "Maintain high hygiene standards across all areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly.",
      "Work independently to complete scheduled cleaning tasks.",
      "Maintain confidentiality and professionalism."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential",
      "Ability to follow health, safety, and hygiene procedures"
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all health & safety and infection-control procedures.",
      "Handle cleaning materials in line with COSHH guidance.",
      "Maintain safe working practices and report hazards immediately."
    ],
    competencies: [
      "Reliability and strong work ethic",
      "Attention to detail",
      "Ability to work independently",
      "Professionalism in sensitive environments",
      "Good communication skills",
      "Respect for confidentiality"
    ],
    featured: true,
    urgent: false,
  },
  {
    id: "teaching-assistant-ealing",
    title: "Teaching Assistant",
    location: "Ealing, West London",
    payRate: "£100 per day",
    contractType: "Temporary",
    category: "Education",
    schedule: "Term-time, flexible daily bookings",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS (Child Barred List)",
    postcodes: ["W5", "W13", "UB1", "UB2", "UB5", "UB6"],
    description: "Support classroom teachers in Ealing primary and secondary schools, helping pupils access the curriculum and encouraging positive learning behaviours.",
    responsibilities: [
      "Provide small-group and 1:1 support under teacher direction.",
      "Assist with lesson preparation, resources and classroom set-up.",
      "Encourage positive behaviour and safeguard pupil wellbeing.",
      "Support pupils with SEN or additional needs where required.",
      "Contribute to pupil progress notes and feedback for teachers.",
      "Follow all school safeguarding and health & safety procedures."
    ],
    qualifications: [
      "Enhanced DBS on the Update Service (or willing to obtain)",
      "Right-to-work documentation",
      "Level 2 or 3 TA qualification preferred (not essential)",
      "Previous experience with children in an education setting"
    ],
    competencies: [
      "Patience and empathy with children",
      "Clear communication with staff, pupils and parents",
      "Reliability and punctuality",
      "Adaptability across year groups and subjects"
    ],
    featured: false,
    urgent: false,
  },
  {
    id: "cover-supervisor-ealing",
    title: "Cover Supervisor",
    location: "Ealing, West London",
    payRate: "£110 per day",
    contractType: "Temporary",
    category: "Education",
    schedule: "Full school day, term-time",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS (Child Barred List)",
    postcodes: ["W5", "W13", "UB1", "UB2", "UB5", "UB6"],
    description: "Cover pre-set lessons in Ealing secondary schools, maintaining classroom order and supporting pupils to complete work left by the absent teacher.",
    responsibilities: [
      "Deliver pre-planned lessons across a range of subjects and key stages.",
      "Manage classroom behaviour in line with the school's policy.",
      "Take the register and monitor pupil engagement.",
      "Feed back to the class teacher and department on the lesson.",
      "Safeguard pupils and report any concerns promptly.",
      "Support with duties (break, lunch, transitions) where required."
    ],
    qualifications: [
      "Enhanced DBS on the Update Service (or willing to obtain)",
      "Right-to-work documentation",
      "Degree or equivalent qualification preferred",
      "Previous experience working with secondary-age pupils"
    ],
    competencies: [
      "Confident classroom presence and behaviour management",
      "Adaptable across subjects and abilities",
      "Professional communication with staff and pupils",
      "Reliability and punctuality"
    ],
    featured: false,
    urgent: false,
  },
  {
    id: "school-admin-ealing",
    title: "School Administrator",
    location: "Ealing, West London",
    payRate: "£13.50 per hour",
    contractType: "Temporary",
    category: "Admin & Office",
    schedule: "Term-time, Monday to Friday",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS (Child Barred List)",
    postcodes: ["W5", "W13", "UB1", "UB2", "UB5", "UB6"],
    description: "Provide front-office and administrative support to Ealing schools, handling reception duties, parent communication and pupil records.",
    responsibilities: [
      "Greet visitors and manage the school reception professionally.",
      "Answer phone, email and parent enquiries.",
      "Maintain pupil records on SIMS, Arbor or Bromcom.",
      "Support attendance, admissions and registers.",
      "Handle general administration and correspondence.",
      "Follow safeguarding and data protection procedures."
    ],
    qualifications: [
      "Enhanced DBS on the Update Service (or willing to obtain)",
      "Right-to-work documentation",
      "Previous school office or admin experience preferred",
      "Confident with MS Office and school MIS systems"
    ],
    competencies: [
      "Strong organisation and prioritisation",
      "Professional written and verbal communication",
      "Discretion with confidential information",
      "Calm, friendly manner with parents and pupils"
    ],
    featured: false,
    urgent: false,
  },
  {
    id: "qualified-teacher-ealing",
    title: "Qualified Teacher",
    location: "Ealing, West London",
    payRate: "£201–£234 per day (MPS1–MPS4, Outer London)",
    contractType: "Temporary",
    category: "Education",
    schedule: "Full school day, daily or long-term bookings",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS (Child Barred List)",
    postcodes: ["W5", "W13", "UB1", "UB2", "UB5", "UB6"],
    description: "Deliver high-quality teaching across Ealing primary and secondary schools on daily supply or longer-term placements. £201–£234 per day, depending on experience and pay scale point (MPS1–MPS4), aligned to the 2026/27 Outer London Main Pay Scale.",
    responsibilities: [
      "Plan and deliver engaging lessons in line with the curriculum.",
      "Assess pupil progress and provide constructive feedback.",
      "Manage classroom behaviour positively and consistently.",
      "Safeguard pupils and follow all school policies.",
      "Liaise with department, SLT and parents as required.",
      "Contribute to a positive, inclusive classroom culture."
    ],
    qualifications: [
      "QTS or equivalent teaching qualification (mandatory)",
      "Enhanced DBS on the Update Service",
      "Right-to-work documentation",
      "Recent classroom experience in the relevant phase or subject"
    ],
    competencies: [
      "Strong subject and pedagogical knowledge",
      "Effective behaviour management",
      "Professional communication and reliability",
      "Adaptability across schools and settings"
    ],
    featured: false,
    urgent: false,
  },
  {
    id: "enhanced-cleaner-catford",
    title: "Cleaner (Enhanced DBS)",
    location: "Catford, South East London",
    payRate: "£13.50 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time, flexible shifts",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS",
    postcodes: ["SE6", "SE13", "SE4"],
    description: "Deliver high-quality enhanced cleaning across public sector and school sites in Catford (SE6), keeping communal and touch-point areas hygienic and safe.",
    responsibilities: [
      "Carry out enhanced touch-point cleaning across all communal areas.",
      "Maintain high hygiene standards across all areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly.",
      "Work independently to complete scheduled cleaning tasks.",
      "Maintain confidentiality and professionalism."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential",
      "Ability to follow health, safety, and hygiene procedures"
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all health & safety and infection-control procedures.",
      "Handle cleaning materials in line with COSHH guidance.",
      "Maintain safe working practices and report hazards immediately."
    ],
    competencies: [
      "Reliability and strong work ethic",
      "Attention to detail",
      "Ability to work independently",
      "Professionalism in sensitive environments"
    ],
    featured: false,
    urgent: false,
  },
  {
    id: "enhanced-cleaner-kington",
    title: "Cleaner (Enhanced DBS)",
    location: "Kington, Herefordshire",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time, flexible weekday shifts",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS",
    postcodes: ["HR5"],
    description: "Deliver high-quality enhanced cleaning at public sector and community sites in Kington (HR5), keeping communal and touch-point areas hygienic and safe.",
    responsibilities: [
      "Carry out enhanced touch-point cleaning across all communal areas.",
      "Maintain high hygiene standards across all areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly.",
      "Work independently to complete scheduled cleaning tasks.",
      "Maintain confidentiality and professionalism."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential",
      "Ability to follow health, safety, and hygiene procedures"
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all health & safety and infection-control procedures.",
      "Handle cleaning materials in line with COSHH guidance.",
      "Maintain safe working practices and report hazards immediately."
    ],
    competencies: [
      "Reliability and strong work ethic",
      "Attention to detail",
      "Ability to work independently",
      "Professionalism in sensitive environments"
    ],
    featured: false,
    urgent: false,
  },
  {
    id: "catering-assistant-worcester",
    title: "Catering Assistant",
    location: "Worcester, Worcestershire",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Hospitality",
    schedule: "Part-time, weekday shifts (breakfast/lunch service)",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS",
    postcodes: ["WR1", "WR2", "WR3", "WR4", "WR5"],
    description: "Support school and public sector kitchens across Worcester (WR1–WR5) with food preparation, service and cleaning to maintain a safe, hygienic catering environment.",
    responsibilities: [
      "Assist with basic food preparation and portioning.",
      "Serve meals to pupils, staff or service users.",
      "Wash dishes, utensils and kitchen equipment.",
      "Clean surfaces and floors in line with food hygiene standards.",
      "Follow allergen and dietary requirements carefully.",
      "Report any hazards, spillages or equipment faults promptly."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Level 2 Food Hygiene preferred (not essential — training given)",
      "Previous kitchen or catering experience preferred"
    ],
    competencies: [
      "Reliability and punctuality",
      "Team-working under time pressure",
      "Attention to hygiene and allergen safety",
      "Friendly, professional manner with service users"
    ],
    featured: false,
    urgent: false,
  },
  {
    id: "enhanced-cleaner-shifnal",
    title: "Cleaner (Enhanced DBS)",
    location: "Shifnal, Shropshire, TF11",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Monday to Friday, 3:30 PM – 6:15 PM",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS",
    closingDate: "2026-06-19",
    postcodes: ["TF11"],
    description: "To deliver high-quality enhanced cleaning services, ensuring all communal and touch-point areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out enhanced touch-point cleaning across all communal areas.",
      "Maintain high hygiene standards across all areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly.",
      "Work independently to complete scheduled cleaning tasks.",
      "Maintain confidentiality and professionalism."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential",
      "Ability to follow health, safety, and hygiene procedures"
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all health & safety and infection-control procedures.",
      "Handle cleaning materials in line with COSHH guidance.",
      "Maintain safe working practices and report hazards immediately."
    ],
    competencies: [
      "Reliability and strong work ethic",
      "Attention to detail",
      "Ability to work independently",
      "Professionalism in sensitive environments",
      "Good communication skills",
      "Respect for confidentiality"
    ],
    featured: false,
    urgent: true,
  },
  {
    id: "enhanced-cleaner-bournemouth",
    title: "Cleaner (Basic DBS)",
    location: "Bournemouth, Dorset",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts available)",
    duration: "Ongoing opportunities",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality enhanced cleaning services, maintaining high hygiene standards particularly across high-touch areas, ensuring the site remains clean, safe, and compliant.",
    responsibilities: [
      "Enhanced cleaning duties across designated areas.",
      "Touch-point cleaning to maintain strict hygiene standards.",
      "General cleaning of allocated areas.",
      "Ensuring all tasks are completed within allocated timeframes.",
      "Working professionally within a sensitive environment.",
      "Reporting any issues, hazards, or maintenance concerns."
    ],
    qualifications: [
      "Valid Basic DBS Certificate (Compulsory)",
      "Previous cleaning experience is desirable but not essential",
      "Ability to follow site instructions and health & safety procedures",
      "Reliable, punctual, and professional"
    ],
    uniform: [
      "Black button-up shirt (ironed)",
      "Black tie",
      "Long smart black suit trousers (must reach shoes)",
      "Black leather shoes (no trainers, boots, loafers, heels, or pumps)",
      "Black long socks",
      "Fingernails must be short",
      "No nail varnish or facial piercings"
    ],
    healthSafety: [
      "Follow all site safety procedures and guidance.",
      "Safely handle cleaning materials and chemicals as instructed.",
      "Report accidents, hazards, or near misses immediately.",
      "Maintain a safe working environment."
    ],
    competencies: [
      "Reliability and punctuality",
      "Attention to detail",
      "Ability to work independently",
      "Professional conduct",
      "Good communication skills"
    ],
    featured: true,
  },
  {
    id: "enhanced-cleaner-kidderminster",
    title: "Cleaner (Enhanced DBS)",
    location: "Kidderminster, Worcestershire",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (less than 30 hours per week)",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS",
    postcodes: ["DY10", "DY11"],
    description: "To deliver high-quality enhanced cleaning services, ensuring all communal and touch-point areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out enhanced touch-point cleaning across all communal areas.",
      "Maintain high hygiene standards across all areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly.",
      "Work independently to complete scheduled cleaning tasks.",
      "Maintain confidentiality and professionalism."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential",
      "Ability to follow health, safety, and hygiene procedures"
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all health & safety and infection-control procedures.",
      "Handle cleaning materials in line with COSHH guidance.",
      "Maintain safe working practices and report hazards immediately."
    ],
    competencies: [
      "Reliability and strong work ethic",
      "Attention to detail",
      "Ability to work independently",
      "Professionalism in sensitive environments",
      "Good communication skills",
      "Respect for confidentiality"
    ],
    featured: false,
  },
  {
    id: "enhanced-cleaner-stourport",
    title: "Cleaner (Enhanced DBS)",
    location: "Stourport-On-Severn, Worcestershire",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (less than 30 hours per week)",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS",
    postcodes: ["DY13"],
    description: "To deliver high-quality enhanced cleaning services, ensuring all communal and touch-point areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out enhanced touch-point cleaning across all communal areas.",
      "Maintain high hygiene standards across all areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly.",
      "Work independently to complete scheduled cleaning tasks.",
      "Maintain confidentiality and professionalism."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential",
      "Ability to follow health, safety, and hygiene procedures"
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all health & safety and infection-control procedures.",
      "Handle cleaning materials in line with COSHH guidance.",
      "Maintain safe working practices and report hazards immediately."
    ],
    competencies: [
      "Reliability and strong work ethic",
      "Attention to detail",
      "Ability to work independently",
      "Professionalism in sensitive environments",
      "Good communication skills",
      "Respect for confidentiality"
    ],
    featured: false,
    urgent: true,
    closingDate: "2026-06-24",
  },
  {
    id: "enhanced-cleaner-oldbury",
    title: "Cleaner (Enhanced DBS)",
    location: "Oldbury, West Midlands",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (less than 30 hours per week)",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS",
    postcodes: ["B68", "B69"],
    description: "To deliver high-quality enhanced cleaning services, ensuring all communal and touch-point areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out enhanced touch-point cleaning across all communal areas.",
      "Maintain high hygiene standards across all areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly.",
      "Work independently to complete scheduled cleaning tasks.",
      "Maintain confidentiality and professionalism."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential",
      "Ability to follow health, safety, and hygiene procedures"
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all health & safety and infection-control procedures.",
      "Handle cleaning materials in line with COSHH guidance.",
      "Maintain safe working practices and report hazards immediately."
    ],
    competencies: [
      "Reliability and strong work ethic",
      "Attention to detail",
      "Ability to work independently",
      "Professionalism in sensitive environments",
      "Good communication skills",
      "Respect for confidentiality"
    ],
    featured: false,
  },
  {
    id: "enhanced-cleaner-basic-dbs",
    title: "Cleaner (Basic DBS)",
    location: "Maidstone, Kent, ME16",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Weekend – 10:00 AM to 12:00 PM",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    postcodes: ["ME16"],
    description: "As part of the Enhanced Cleaning Team, you play a vital role in maintaining a clean, safe environment. You'll ensure that all designated areas are cleaned to the highest standards and in compliance with health, safety, and COSHH regulations.",
    responsibilities: [
      "Carry out regular and enhanced cleaning duties across the building.",
      "Increase the frequency of cleaning in high-touch areas (door handles, switches, kitchens, washrooms).",
      "Follow all COSHH procedures when handling cleaning products and equipment.",
      "Maintain cleaning equipment safely and in good condition.",
      "Report maintenance or safety issues to the site supervisor immediately.",
      "Adhere to site-specific safety procedures."
    ],
    qualifications: [
      "Basic DBS certificate (must be presented on site)",
      "Valid photo ID (must be presented on site)",
      "Previous cleaning or facilities maintenance experience preferred but not essential"
    ],
    uniform: [
      "Black button-up shirt (ironed)",
      "Black tie",
      "Smart black suit trousers (no jeans or skinny styles)",
      "Black leather shoes (no trainers or heels)",
      "Black long socks",
      "No nail varnish or facial piercings permitted"
    ],
    healthSafety: [
      "Follow all Health & Safety and COSHH guidelines when cleaning.",
      "Maintain high standards of hygiene to limit infection spread.",
      "Bring DBS certificate and photo ID for site verification."
    ],
    competencies: [
      "Strong attention to detail and personal responsibility",
      "Awareness of hygiene and infection-control practices",
      "Ability to follow instructions and work independently",
      "Punctuality and reliability",
      "Commitment to high standards of professionalism"
    ],
    featured: false,
  },
  {
    id: "basic-cleaner-canterbury",
    title: "Cleaner (Basic DBS)",
    location: "Canterbury CT1",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time | 4:30pm – 7:00pm",
    duration: "15/12/2025 – 31/12/2025 (excluding Christmas Day & Boxing Day)",
    dbsRequired: "Basic DBS",
    closingDate: "2025-12-14",
    description: "To provide high-quality general office and customer-facing cleaning services during the specified cover period, ensuring all communal and operational areas remain clean, safe, and presentable for staff and visitors. This temporary cover position offers immediate start with competitive hourly rates.",
    responsibilities: [
      "Clean and maintain general office and customer-facing areas.",
      "Touch-point cleaning across all key communal surfaces.",
      "Empty bins, replenish supplies, and maintain hygiene standards.",
      "Ensure cleaning tasks are completed within the designated timeframe.",
      "Report any maintenance or safety issues promptly.",
      "Follow all site protocols and security procedures."
    ],
    qualifications: [
      "Basic DBS Certificate (valid and in-date)",
      "Proof of right to work in the UK",
      "Proof of address and identity documentation"
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark colour trousers",
      "Non-slip safety shoes",
      "Black warm jumper (bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow COSHH procedures where relevant.",
      "Use cleaning products safely and correctly.",
      "Wear required PPE at all times.",
      "Maintain a safe working environment for staff and visitors."
    ],
    competencies: [
      "Reliability and punctuality",
      "Strong attention to detail",
      "Ability to work independently",
      "Professional and polite manner in customer-facing environments",
      "Understanding of hygiene and safety standards"
    ],
    featured: true,
  },
  {
    id: "hospitality-manager",
    title: "Hospitality Manager",
    location: "London, Central",
    payRate: "£35,000 - £42,000/year",
    contractType: "Permanent",
    category: "Hospitality",
    description: "Lead and manage hospitality operations for a premium venue. Oversee front-of-house staff, ensure exceptional guest experiences, and maintain high service standards.",
    featured: true,
  },
  {
    id: "call-centre-operator",
    title: "Customer Service Operator",
    location: "Manchester, Greater Manchester",
    payRate: "£22,000 - £25,000/year",
    contractType: "Permanent",
    category: "Administration",
    schedule: "Monday to Friday, 9am-5pm",
    description: "Handle customer enquiries, manage administrative tasks, and provide professional support. Full training provided.",
  },
  {
    id: "facilities-technician",
    title: "Facilities Maintenance Technician",
    location: "Leeds, Yorkshire",
    payRate: "£16.50/hour",
    contractType: "Contract",
    category: "Facilities Maintenance",
    schedule: "Full-time, 40 hours per week",
    duration: "6 month contract",
    description: "Carry out routine maintenance, repairs, and installations across commercial facilities. HVAC and electrical experience preferred.",
  },
  {
    id: "care-assistant",
    title: "Domestic Care Assistant",
    location: "Bromley, London",
    payRate: "£13.20/hour",
    contractType: "Permanent",
    category: "Domestic Services",
    schedule: "Part-time and full-time available",
    dbsRequired: "Enhanced DBS",
    description: "Provide compassionate care and support to clients in their homes. Assist with daily living activities and maintain client dignity.",
  },
  {
    id: "store-manager",
    title: "Store Manager",
    location: "Bristol, South West",
    payRate: "£32,000 - £38,000/year",
    contractType: "Permanent",
    category: "Retail",
    description: "Manage daily store operations, lead a team of 15+ staff, drive sales performance, and ensure excellent customer experiences.",
    featured: true,
  },
  {
    id: "kitchen-porter",
    title: "Kitchen Porter",
    location: "Oxford, Oxfordshire",
    payRate: "£12.71/hour",
    contractType: "Temporary",
    category: "Hospitality",
    schedule: "Evening shifts, 5pm-11pm",
    duration: "Immediate start, ongoing",
    description: "Support kitchen operations by maintaining cleanliness, washing dishes, and assisting chefs with basic food preparation tasks.",
  },
  {
    id: "contact-centre-supervisor",
    title: "Administration Team Supervisor",
    location: "Liverpool, Merseyside",
    payRate: "£28,000 - £32,000/year",
    contractType: "Permanent",
    category: "Administration",
    description: "Supervise a team of administrative staff, monitor performance, provide coaching, and ensure quality service delivery.",
  },
  {
    id: "retrofit-assessor-london",
    title: "Retrofit Assessor / Adviser (PAS 2035)",
    location: "London, Greater London",
    payRate: "£35,000 - £45,000/year",
    contractType: "Permanent",
    category: "Construction",
    description: "Conduct retrofit assessments aligned with PAS 2035 and TrustMark standards. Evaluate properties for energy efficiency improvements and provide recommendations for retrofit measures.",
    responsibilities: [
      "Conduct property assessments following PAS 2035 guidelines",
      "Prepare detailed retrofit assessment reports",
      "Advise clients on energy efficiency improvements",
      "Liaise with retrofit coordinators and installers",
      "Maintain TrustMark compliance documentation"
    ],
    qualifications: [
      "Level 5 Diploma in Retrofit Coordination and Risk Management or equivalent",
      "DEA or HHIC qualification preferred",
      "Understanding of PAS 2035:2019 framework",
      "TrustMark registration or eligibility"
    ],
    featured: true,
  },
  {
    id: "skilled-labourer-manchester",
    title: "Skilled Labourer",
    location: "Manchester, Greater Manchester",
    payRate: "£14.50 - £16.50/hour",
    contractType: "Temporary",
    category: "Construction",
    schedule: "Full-time, flexible shifts",
    duration: "Ongoing projects",
    description: "Provide skilled labour support on construction and retrofit sites. Assist with various trades and ensure site safety and cleanliness.",
    responsibilities: [
      "Assist skilled tradespeople with construction tasks",
      "Handle materials and equipment safely",
      "Maintain site cleanliness and organisation",
      "Follow health and safety procedures",
      "Support retrofit installation works"
    ],
    qualifications: [
      "Valid CSCS card",
      "Previous construction site experience",
      "Manual handling certification preferred",
      "Ability to work in a team environment"
    ],
  },
  {
    id: "post-works-cleaner-leeds",
    title: "Post-Maintenance & End-of-Works Cleaner",
    location: "Leeds, Yorkshire",
    payRate: "£12.50 - £13.50/hour",
    contractType: "Temporary",
    category: "Construction",
    schedule: "Part-time and full-time available",
    duration: "Project-based",
    description: "Provide specialist cleaning services for construction and maintenance sites after works completion. Ensure properties are left clean and ready for handover.",
    responsibilities: [
      "Deep clean properties following construction or maintenance works",
      "Remove dust, debris, and construction residue",
      "Clean windows, fixtures, and fittings",
      "Prepare properties for client handover",
      "Dispose of waste materials appropriately"
    ],
    qualifications: [
      "Previous post-construction cleaning experience preferred",
      "Knowledge of cleaning materials and equipment",
      "Physical fitness for demanding cleaning tasks",
      "Attention to detail and quality standards"
    ],
  },
  {
    id: "cleaner-tonbridge",
    title: "Cleaner (Basic DBS)",
    location: "Tonbridge, Kent",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-lambeth",
    title: "Cleaner (Basic DBS)",
    location: "Lambeth, London",
    postcodes: ["SW2", "SW4", "SW8", "SW9", "SE11", "SE24", "SE27"],
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "enhanced-cleaner-tulse-hill",
    title: "Cleaner (Basic DBS)",
    location: "Tulse Hill, London",
    postcodes: ["SE21", "SE24"],
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Weekend shifts, 7:00 am – 1:00 pm",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "Enhanced cleaner role at our Tulse Hill site (147 Tulse Hill, London SE21). Deliver high-quality touch-point cleaning across communal areas, ensuring all spaces remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out touch-point cleaning across all communal areas.",
      "Maintain hygiene standards in line with site expectations.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly.",
      "Work independently to complete scheduled cleaning tasks."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
    featured: true,
    
  },
  {
    id: "cleaner-tunbridge-wells",
    title: "Cleaner (Basic DBS)",
    location: "Tunbridge Wells, Kent",
    postcodes: ["TN1", "TN2", "TN3", "TN4"],
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Monday to Friday, 6:00 pm – 9:00 pm",
    duration: "Ongoing (start date 11 May)",
    dbsRequired: "Basic DBS",
    description: "Evening cleaner role in Tunbridge Wells, Kent. Deliver high-quality cleaning across communal and touch-point areas, ensuring the site remains hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and touch-point cleaning across all areas.",
      "Maintain hygiene standards in line with site expectations.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly.",
      "Work independently to complete scheduled cleaning tasks."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-smethwick",
    title: "Cleaner (Basic DBS)",
    location: "Smethwick, West Midlands",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    postcodes: ["B66", "B67"],
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-brierley-hill",
    title: "Cleaner (Basic DBS)",
    location: "Brierley Hill, West Midlands",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    postcodes: ["DY5"],
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-stourbridge",
    title: "Enhanced Cleaner (Enhanced DBS)",
    location: "Stourbridge, West Midlands",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS",
    postcodes: ["DY8"],
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
    featured: true,
  },
  {
    id: "cleaner-brighton",
    title: "Cleaner (Basic DBS)",
    location: "Brighton, East Sussex",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-st-leonards",
    title: "Cleaner (Basic DBS)",
    location: "St Leonards-on-Sea, East Sussex",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-beckenham",
    title: "Cleaner (Basic DBS)",
    location: "Beckenham, London",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-croydon",
    title: "Cleaner (Basic DBS)",
    location: "Croydon, London",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-hove",
    title: "Cleaner (Basic DBS)",
    location: "Hove, East Sussex",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-portsmouth",
    title: "Cleaner (Basic DBS)",
    location: "Portsmouth, Hampshire",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-hastings",
    title: "Cleaner (Basic DBS)",
    location: "Hastings, East Sussex",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-ashford",
    title: "Cleaner (Basic DBS)",
    location: "Ashford, Kent",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-lewisham",
    title: "Cleaner (Basic DBS)",
    location: "Lewisham, London",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-milton-keynes",
    title: "Cleaner (Basic DBS)",
    location: "Milton Keynes, Buckinghamshire",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-newport-iow",
    title: "Cleaner (Basic DBS)",
    location: "Newport, Isle of Wight",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-orpington",
    title: "Cleaner (Basic DBS)",
    location: "Orpington, Kent",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-banbury",
    title: "Cleaner (Basic DBS)",
    location: "Banbury, Oxfordshire",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-caerphilly",
    title: "Cleaner (Basic DBS)",
    location: "Caerphilly, South Wales",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-weston-super-mare",
    title: "Cleaner (Basic DBS)",
    location: "Weston-super-Mare, Somerset",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-coventry",
    title: "Cleaner (Enhanced DBS)",
    location: "Coventry, West Midlands",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS",
    postcodes: ["CV3", "CV6"],
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-wolverhampton",
    title: "Cleaner (Enhanced DBS)",
    location: "Wolverhampton, West Midlands",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "cleaner-newcastle-under-lyme",
    title: "Cleaner (Enhanced DBS)",
    location: "Newcastle-under-Lyme, Staffordshire",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS",
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  // School Cleaning Jobs - Unique locations only
  {
    id: "school-cleaner-harborne",
    title: "Cleaner (Enhanced DBS)",
    location: "Harborne, Birmingham",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "3:30pm - 5:30pm",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS",
    postcodes: ["B17"],
    description: "To deliver high-quality cleaning services, ensuring all areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "school-cleaner-hagley",
    title: "Cleaner (Enhanced DBS)",
    location: "Hagley, Worcestershire, DY8",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "3:30pm - 6:00pm",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS",
    datePosted: "2026-01-15",
    postcodes: ["DY8"],
    description: "To provide reliable, ongoing afternoon cleaning in Hagley, Worcestershire (DY8), maintaining high standards of hygiene and safety across communal and operational areas.",
    responsibilities: [
      "Carry out general and enhanced cleaning duties.",
      "Maintain hygiene standards across communal areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential"
    ],
  },
  {
    id: "rsw-doncaster-1",
    title: "Children's Residential Support Worker",
    location: "Doncaster, Yorkshire",
    payRate: "£12.71 - £15.00 per hour",
    contractType: "Permanent",
    category: "Healthcare & Support Work",
    schedule: "Flexible shifts (days, nights, weekends)",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS (Child Workforce)",
    postcodes: ["DN1", "DN2", "DN3", "DN4", "DN5", "DN6", "DN7", "DN8", "DN9", "DN10", "DN11", "DN12"],
    description: "PAMP Recruitment Agency LTD is urgently recruiting experienced Children's Residential Support Workers to join children's homes in Doncaster. You will provide high-quality care in a safe and nurturing environment, helping children with daily routines and supporting their emotional and social development. This is urgent work with immediate start opportunities.",
    responsibilities: [
      "Support children in daily routines and activities.",
      "Create a safe, stable, and nurturing environment.",
      "Manage challenging behaviour calmly and professionally.",
      "Support emotional and social development.",
      "Work collaboratively with other care staff."
    ],
    qualifications: [
      "Enhanced DBS (Child Workforce) (mandatory)",
      "Right to Work in the UK",
      "Minimum 6 months residential care experience",
      "CV with full employment history",
      "2 care-related references",
      "Safeguarding Training",
      "Manual Handling certification",
      "Basic First Aid certification",
      "Level 3 Diploma in Children & Young People (highly desirable)",
      "PMVA / CPI restraint training (highly desirable)"
    ],
    competencies: [
      "Reliability and strong work ethic",
      "Calm and professional under pressure",
      "Empathy and patience with young people",
      "Ability to work independently and as part of a team",
      "Good communication skills",
      "Respect for confidentiality"
    ],
    featured: true,
  },
  {
    id: "rsw-doncaster-2",
    title: "Children's Residential Support Worker",
    location: "Doncaster, Yorkshire",
    payRate: "£12.71 - £15.00 per hour",
    contractType: "Permanent",
    category: "Healthcare & Support Work",
    schedule: "Flexible shifts (days, nights, weekends)",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS (Child Workforce)",
    postcodes: ["DN1", "DN2", "DN3", "DN4", "DN5", "DN6", "DN7", "DN8", "DN9", "DN10", "DN11", "DN12"],
    description: "PAMP Recruitment Agency LTD is urgently recruiting experienced Children's Residential Support Workers to join children's homes in Doncaster. You will provide high-quality care in a safe and nurturing environment, helping children with daily routines and supporting their emotional and social development. This is urgent work with immediate start opportunities.",
    responsibilities: [
      "Support children in daily routines and activities.",
      "Create a safe, stable, and nurturing environment.",
      "Manage challenging behaviour calmly and professionally.",
      "Support emotional and social development.",
      "Work collaboratively with other care staff."
    ],
    qualifications: [
      "Enhanced DBS (Child Workforce) (mandatory)",
      "Right to Work in the UK",
      "Minimum 6 months residential care experience",
      "CV with full employment history",
      "2 care-related references",
      "Safeguarding Training",
      "Manual Handling certification",
      "Basic First Aid certification",
      "Level 3 Diploma in Children & Young People (highly desirable)",
      "PMVA / CPI restraint training (highly desirable)"
    ],
    competencies: [
      "Reliability and strong work ethic",
      "Calm and professional under pressure",
      "Empathy and patience with young people",
      "Ability to work independently and as part of a team",
      "Good communication skills",
      "Respect for confidentiality"
    ],
    featured: true,
  },
  {
    id: "rsw-doncaster-3",
    title: "Children's Residential Support Worker",
    location: "Doncaster, Yorkshire",
    payRate: "£12.71 - £15.00 per hour",
    contractType: "Permanent",
    category: "Healthcare & Support Work",
    schedule: "Flexible shifts (days, nights, weekends)",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS (Child Workforce)",
    description: "PAMP Recruitment Agency LTD is urgently recruiting experienced Children's Residential Support Workers to join children's homes in Doncaster. You will provide high-quality care in a safe and nurturing environment, helping children with daily routines and supporting their emotional and social development. Immediate start opportunities available.",
    responsibilities: [
      "Support children in daily routines and activities",
      "Create a safe, stable, and nurturing environment",
      "Manage challenging behaviour calmly and professionally",
      "Support emotional and social development",
      "Work collaboratively with other care staff"
    ],
    qualifications: [
      "Enhanced DBS (Child Workforce) required",
      "Right to Work in the UK",
      "Minimum 6 months residential care experience",
      "CV with full employment history",
      "2 care-related references",
      "Safeguarding Training",
      "Manual Handling certification",
      "Basic First Aid",
      "Level 3 Diploma in Children & Young People (desirable)",
      "PMVA / CPI restraint training (desirable)"
    ],
    postcodes: ["DN1", "DN2", "DN3", "DN4", "DN5", "DN6", "DN7", "DN8", "DN9", "DN10", "DN11", "DN12"],
    competencies: [
      "Reliability and strong work ethic",
      "Calm and professional under pressure",
      "Empathy and patience with young people",
      "Ability to work independently and as part of a team",
      "Good communication skills",
      "Respect for confidentiality"
    ],
    featured: true,
  },
  {
    id: "care-manager-lewisham",
    title: "Care Manager",
    location: "Lewisham, London",
    payRate: "£15.38 - £16.41 per hour",
    contractType: "Permanent",
    category: "Healthcare & Support Work",
    schedule: "Full-time",
    duration: "Permanent - immediate start",
    dbsRequired: "Enhanced DBS",
    postcodes: ["SE13", "SE14", "SE6", "SE4"],
    description: "PAMP Recruitment Agency LTD is recruiting an experienced Care Manager to oversee domiciliary care services across Lewisham, South East London. You will lead care teams, ensure CQC compliance and maintain high-quality care delivery for clients in their own homes. Immediate start available.",
    responsibilities: [
      "Lead daily domiciliary care operations.",
      "Ensure full CQC compliance across the service.",
      "Supervise Care Coordinators and care staff.",
      "Manage audits, quality checks and continuous improvements.",
      "Oversee safeguarding concerns and investigations.",
      "Support recruitment, induction and ongoing staff training.",
      "Maintain relationships with families, clients and other professionals."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Proven experience in domiciliary care management",
      "Strong working knowledge of CQC regulations",
      "Leadership and team management skills",
      "Strong communication and organisation ability",
      "Experience with electronic care systems (preferred)"
    ],
    healthSafety: [
      "Maintain safeguarding and infection-control standards.",
      "Ensure staff follow safe working practices in clients' homes.",
      "Report and investigate accidents, hazards and near misses."
    ],
    competencies: [
      "Strong leadership and decision-making",
      "Calm and professional under pressure",
      "Empathy and respect for vulnerable adults",
      "Excellent communication skills",
      "Commitment to safeguarding",
      "Respect for confidentiality"
    ],
    featured: true,
  },
  {
    id: "home-care-assistant-driver-cheshunt",
    title: "Home Care Assistant (Driver)",
    location: "Cheshunt, Hertfordshire",
    payRate: "£12.71 - £14.50 per hour (dependent on experience)",
    contractType: "Permanent",
    category: "Healthcare & Support Work",
    schedule: "Full-time / Part-time",
    duration: "Permanent - immediate start",
    dbsRequired: "Enhanced DBS",
    postcodes: ["EN7", "EN8", "EN10", "EN11"],
    description: "PAMP Recruitment Agency LTD is urgently recruiting Home Care Assistants with a full UK driving licence to support vulnerable adults in their own homes across Hertfordshire. You will travel between clients to provide essential personal care and companionship, helping individuals live independently and safely. Full training is provided.",
    responsibilities: [
      "Travel between clients' homes across Hertfordshire.",
      "Provide personal care including washing, dressing and toileting.",
      "Support medication prompts (training provided).",
      "Assist with meal preparation and daily living tasks.",
      "Provide companionship and emotional support.",
      "Follow care plans and safeguarding procedures.",
      "Record accurate daily care notes."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Full UK driving licence with access to a vehicle (mandatory)",
      "Right-to-work documentation",
      "Caring, reliable and professional attitude",
      "Ability to work independently",
      "No previous experience required - full training provided"
    ],
    healthSafety: [
      "Follow safeguarding and infection-control procedures.",
      "Use safe moving and handling techniques.",
      "Report safeguarding concerns, accidents and hazards immediately."
    ],
    competencies: [
      "Reliability and punctuality",
      "Empathy and patience with vulnerable adults",
      "Ability to work independently in clients' homes",
      "Good communication skills",
      "Respect for dignity and confidentiality",
      "Strong driving and time-management skills"
    ],
    featured: true,
    urgent: false,
  },
  {
    id: "domiciliary-care-worker-driver-cheshunt",
    title: "Domiciliary Care Worker (Driver)",
    location: "Cheshunt, Hertfordshire",
    payRate: "£12.71 - £14.50 per hour (dependent on experience)",
    contractType: "Permanent",
    category: "Healthcare & Support Work",
    schedule: "Tuesday & Thursday: 4:30 pm - 11:30 pm | Saturday: 12:30 pm - 11:30 pm",
    duration: "Permanent - immediate start",
    dbsRequired: "Enhanced DBS",
    postcodes: ["EN7", "EN8", "EN10", "EN11"],
    description: "PAMP Recruitment Agency LTD is recruiting Domiciliary Care Workers with a full UK driving licence to deliver personal care to vulnerable adults in their own homes across Hertfordshire. Set evening and weekend hours - Tuesday and Thursday 4:30 pm to 11:30 pm, plus Saturday 12:30 pm to 11:30 pm. Full training is provided.",
    responsibilities: [
      "Travel between clients' homes across Hertfordshire during evening and weekend shifts.",
      "Provide personal care including washing, dressing and toileting.",
      "Support medication prompts (training provided).",
      "Assist with meal preparation, hydration and daily living tasks.",
      "Provide companionship and emotional support during longer Saturday shifts.",
      "Follow care plans and safeguarding procedures.",
      "Record accurate daily care notes after each visit."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Full UK driving licence with access to a vehicle (mandatory)",
      "Right-to-work documentation",
      "Availability for Tuesday, Thursday evening and Saturday shifts",
      "Caring, reliable and professional attitude",
      "Ability to work independently",
      "No previous experience required - full training provided"
    ],
    healthSafety: [
      "Follow safeguarding and infection-control procedures.",
      "Use safe moving and handling techniques.",
      "Report safeguarding concerns, accidents and hazards immediately."
    ],
    competencies: [
      "Reliability and punctuality across evening and weekend shifts",
      "Empathy and patience with vulnerable adults",
      "Ability to work independently in clients' homes",
      "Good communication skills",
      "Respect for dignity and confidentiality",
      "Strong driving and time-management skills"
    ],
    featured: true,
    urgent: false,
  },
  {
    id: "care-coordinator-dorset",
    title: "Care Coordinator",
    location: "Dorchester, Dorset",
    payRate: "£12.71 - £14.50 per hour (dependent on experience)",
    contractType: "Permanent",
    category: "Healthcare & Support Work",
    schedule: "Full-time",
    duration: "Permanent",
    dbsRequired: "Enhanced DBS",
    postcodes: ["DT1", "DT2", "DT6", "DT10", "DT11"],
    description: "PAMP Recruitment Agency LTD is seeking an experienced Care Coordinator to manage domiciliary care operations across Dorset. You will ensure care visits are scheduled effectively, services run smoothly and clients receive consistent, high-quality support. Applicants must be based in Dorset.",
    responsibilities: [
      "Manage care rotas and weekly scheduling.",
      "Allocate care workers to clients based on need and location.",
      "Respond to emergencies, cancellations and last-minute changes.",
      "Communicate with families, clients and care staff.",
      "Maintain accurate care records and documentation.",
      "Support compliance with CQC standards."
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Must be based in Dorset",
      "Proven experience in domiciliary care coordination",
      "Strong organisation and communication skills",
      "Ability to work under pressure",
      "Experience with IT and electronic rota systems"
    ],
    healthSafety: [
      "Support safeguarding procedures across the service.",
      "Ensure staff allocations follow safe working practices.",
      "Escalate risks, incidents and complaints promptly."
    ],
    competencies: [
      "Highly organised and detail-oriented",
      "Calm and professional under pressure",
      "Strong communication skills",
      "Empathy and respect for vulnerable adults",
      "Reliable and adaptable",
      "Respect for confidentiality"
    ],
    featured: true,
  },
  {
    id: "enhanced-cleaner-bridgwater-ta6",
    title: "Cleaner (Basic DBS)",
    location: "Bridgwater, Somerset, TA6",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (flexible shifts)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    datePosted: "2026-06-04",
    postcodes: ["TA6", "TA7"],
    description: "To provide reliable, ongoing enhanced cleaning in Bridgwater, Somerset (TA6), keeping communal areas and frequent touch points hygienic, safe and well maintained.",
    responsibilities: [
      "Carry out enhanced touch-point cleaning across all communal areas.",
      "Maintain high hygiene standards across all areas.",
      "Use cleaning materials and equipment safely.",
      "Report hazards or maintenance issues promptly.",
      "Work independently to complete scheduled cleaning tasks.",
      "Maintain confidentiality and professionalism."
    ],
    qualifications: [
      "Basic DBS certificate (mandatory)",
      "Right-to-work documentation",
      "Previous cleaning experience preferred but not essential",
      "Ability to follow health, safety, and hygiene procedures"
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all health & safety and infection-control procedures.",
      "Handle cleaning materials in line with COSHH guidance.",
      "Maintain safe working practices and report hazards immediately."
    ],
    competencies: [
      "Reliability and strong work ethic",
      "Attention to detail",
      "Ability to work independently",
      "Professionalism in sensitive environments",
      "Good communication skills",
      "Respect for confidentiality"
    ],
    featured: true,
  },
  {
    id: "chef-de-partie-bournemouth-bh5",
    title: "Chef de Partie – Bournemouth (Enhanced DBS Required)",
    location: "Boscombe, Bournemouth, BH5",
    postcodes: ["BH5"],
    payRate: "Competitive (contact PAMP Recruitment for details)",
    contractType: "Temporary",
    category: "Hospitality",
    schedule: "Friday 12th June, 12:00 PM – 6:00 PM",
    duration: "Temporary agency shift",
    dbsRequired: "Enhanced DBS",
    description:
      "PAMP Recruitment is seeking an experienced and reliable Chef de Partie to support catering operations at an approved premises in Bournemouth. The role requires strong food safety, kitchen management, and hospitality service standards, preparing and delivering high-quality food while maintaining excellent hygiene and operational standards.",
    responsibilities: [
      "Prepare and serve restaurant and hospitality meals to a high standard.",
      "Assist with menu planning, costing, and stock control.",
      "Maintain food quality, presentation, and hygiene standards.",
      "Ensure compliance with food safety legislation and company procedures.",
      "Monitor food costs and contribute to achieving budget targets.",
      "Order supplies from approved suppliers and manage inventory effectively.",
      "Support recruitment, training, and development of kitchen staff.",
      "Maintain accurate records and complete required documentation.",
      "Ensure compliance with Health & Safety, COSHH, Food Safety, Fire Safety, and Accident Reporting procedures.",
      "Deliver excellent customer service and meet client expectations.",
      "Participate in continuous improvement initiatives and company promotions.",
      "Promote a safe working environment for yourself, colleagues, and visitors.",
    ],
    qualifications: [
      "Previous Chef de Partie experience within a hospitality or catering environment.",
      "Enhanced DBS Certificate (must be brought to site).",
      "Valid photographic ID.",
      "Level 2 Food Safety Certificate.",
      "Food Allergens Certificate.",
      "Strong knowledge of food hygiene and health & safety regulations.",
      "Ability to work independently and as part of a team.",
    ],
    uniform: [
      "White Chef Jacket",
      "Black Trousers",
      "Steel Toe Safety Shoes",
      "Chef Hat or Skull Cap",
      "Butcher's Apron",
      "Personal chef knives may be brought to site but must be transported safely in a suitable knife roll or hard case. Knives must never be wrapped in cloth.",
    ],
    healthSafety: [
      "Enhanced DBS and photo ID must be presented on arrival.",
      "Comply with COSHH, Food Safety, Fire Safety, and Accident Reporting procedures at all times.",
    ],
    featured: true,
  },
  {
    id: "support-worker-greenwich-se3",
    title: "Support Worker / Personal Assistant",
    location: "Greenwich, London SE3",
    payRate: "£13.00 per hour",
    contractType: "Temporary",
    category: "Healthcare & Support Work",
    schedule: "Up to 30 hours per week, typically up to 5 days per week including some weekend shifts (4–6 hour daily shifts)",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS",
    postcodes: ["SE3"],
    description: "We are looking to hire a Support Worker / Personal Assistant to support a neurodiverse young man living independently in Greenwich, London SE3. We are seeking one or two Support Workers to provide up to 30 hours of support per week — suitable for one dedicated individual or a job-share arrangement. The successful applicant will provide compassionate, trauma-informed support focused on building confidence, independence, wellbeing and community involvement. You will support a bright 27-year-old autistic man who lives independently and has additional mental health needs and a history of substance misuse. He is looking for someone caring, patient and understanding who can provide companionship, encouragement and practical support as he continues his rehabilitation journey. This role focuses on rehabilitation, personal development and general support — personal care is not required, and he is a keen cook.",
    responsibilities: [
      "Support, prompting and encouragement with personal care and medication adherence.",
      "Support, prompting and encouragement to maintain a clean and tidy home environment.",
      "Assistance with planning daily routines, budgeting, shopping, meal planning and healthy lifestyle choices.",
      "Support with organising and attending appointments.",
      "Encouragement and motivation to participate in local community activities and interests, including playing pool, going to the gym, pub quizzes, music events and days out.",
      "Support to reconnect with personal interests and talents, including art, music and cooking.",
      "Companionship and emotional support to help build confidence, self-esteem, independence and hope for the future.",
    ],
    qualifications: [
      "Enhanced DBS certificate (mandatory)",
      "Right to work in the UK",
      "Verifiable references",
      "Professional and reliable attitude",
      "Strong commitment to confidentiality and safeguarding",
    ],
    uniform: [
      "Smart, casual and appropriate clothing suitable for community-based support",
      "Comfortable, closed-toe footwear",
    ],
    healthSafety: [
      "Follow trauma-informed and person-centred support practice at all times.",
      "Adhere to safeguarding, confidentiality and lone-working procedures.",
      "Report any concerns regarding wellbeing, safety or safeguarding promptly.",
    ],
    competencies: [
      "Compassionate, patient and non-judgemental approach",
      "Reliability and strong professional boundaries",
      "Good communication and active listening skills",
      "Ability to motivate, encourage and build trust",
    ],
    featured: false,
    urgent: false,
  },
  {
    id: "cleaning-operative-salisbury-basic",
    title: "Cleaner (Basic DBS)",
    location: "Salisbury, Wiltshire, SP1",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (less than 30 hours per week)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    closingDate: "2026-12-31",
    postcodes: ["SP1"],
    description: "To deliver high-quality cleaning services in Salisbury, ensuring all communal and touch-point areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "General cleaning of offices, corridors and communal areas.",
      "Cleaning and restocking washroom facilities.",
      "Vacuuming, mopping, dusting and surface sanitising.",
      "Emptying waste bins and managing waste segregation.",
      "Following all site health and safety and COSHH procedures."
    ],
    qualifications: [
      "Previous cleaning experience preferred.",
      "Basic DBS check required (must be held before starting).",
      "Ability to work unsupervised and follow a cleaning schedule.",
      "Punctual, reliable and presentable — strict site access procedures apply.",
      "Right to work in the UK."
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (to bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all site health and safety procedures.",
      "Handle cleaning materials in line with COSHH guidance.",
      "Maintain safe working practices and report hazards immediately."
    ],
    competencies: [
      "Reliability and strong work ethic",
      "Attention to detail",
      "Ability to work independently",
      "Professionalism on government sites",
      "Good communication skills"
    ],
    featured: true,
    pinned: true,
    urgent: false,
  },
  {
    id: "early-morning-cleaner-basildon-enhanced",
    title: "Cleaner (Enhanced DBS)",
    location: "Basildon, Essex, SS15",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (less than 30 hours per week)",
    duration: "Ongoing",
    dbsRequired: "Enhanced DBS",
    closingDate: "2026-12-31",
    postcodes: ["SS15"],
    description: "To deliver high-quality enhanced cleaning services in Basildon, ensuring all communal and touch-point areas remain hygienic, safe, and well maintained.",
    responsibilities: [
      "Cleaning offices, communal areas and circulation routes before occupancy.",
      "Washroom cleaning and consumables restocking.",
      "Vacuuming, mopping and surface sanitising.",
      "Waste removal and bin emptying.",
      "Working to site security and health and safety procedures at all times."
    ],
    qualifications: [
      "Enhanced DBS check required (must be held and valid before starting).",
      "Own reliable transport recommended due to the early start.",
      "Previous cleaning experience, ideally in a secure environment.",
      "Punctual and dependable.",
      "Right to work in the UK."
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (to bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all site health and safety procedures.",
      "Handle cleaning materials in line with COSHH guidance.",
      "Maintain safe working practices and report hazards immediately."
    ],
    competencies: [
      "Reliability and strong work ethic",
      "Attention to detail",
      "Ability to work independently",
      "Professionalism on government sites",
      "Punctuality for early morning shifts"
    ],
    featured: true,
    pinned: true,
    urgent: false,
  },
  {
    id: "cleaner-staines-basic-dbs",
    title: "Cleaner (Basic DBS)",
    location: "Staines, Surrey, TW18",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (less than 30 hours per week)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    closingDate: "2026-09-18",
    datePosted: "2026-09-16",
    postcodes: ["TW18"],
    description: "To provide reliable, ongoing part-time cleaning in Staines, Surrey (TW18), keeping offices, corridors, communal areas and washroom facilities hygienic, safe and well maintained.",
    responsibilities: [
      "General cleaning of offices, corridors and communal areas.",
      "Cleaning and restocking washroom facilities.",
      "Vacuuming, mopping, dusting and surface sanitising.",
      "Emptying waste bins and managing waste segregation.",
      "Following all site health and safety and COSHH procedures."
    ],
    qualifications: [
      "Previous cleaning experience preferred.",
      "Basic DBS check required (must be held before starting).",
      "Ability to work unsupervised and follow a cleaning schedule.",
      "Punctual, reliable and presentable.",
      "Right to work in the UK."
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (to bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all site health and safety procedures.",
      "Handle cleaning materials in line with COSHH guidance.",
      "Maintain safe working practices and report hazards immediately."
    ],
    competencies: [
      "Reliability and strong work ethic",
      "Attention to detail",
      "Ability to work independently",
      "Good communication skills"
    ],
    featured: true,
    pinned: true,
    urgent: true,
  },
  {
    id: "cleaner-london-ec1v-basic-dbs",
    title: "Cleaner (Basic DBS)",
    location: "Angel, Greater London, EC1V",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (less than 30 hours per week)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    closingDate: "2026-12-31",
    datePosted: "2026-09-29",
    postcodes: ["EC1V"],
    description: "To provide reliable, ongoing part-time cleaning in Angel, Greater London (EC1V), keeping offices, corridors, communal areas and washroom facilities hygienic, safe and well maintained.",
    responsibilities: [
      "General cleaning of offices, corridors and communal areas.",
      "Cleaning and restocking washroom facilities.",
      "Vacuuming, mopping, dusting and surface sanitising.",
      "Emptying waste bins and managing waste segregation.",
      "Following all site health and safety and COSHH procedures."
    ],
    qualifications: [
      "Previous cleaning experience preferred.",
      "Basic DBS check required (must be held before starting).",
      "Ability to work unsupervised and follow a cleaning schedule.",
      "Punctual, reliable and presentable.",
      "Right to work in the UK."
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (to bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all site health and safety procedures.",
      "Handle cleaning materials in line with COSHH guidance.",
      "Maintain safe working practices and report hazards immediately."
    ],
    competencies: [
      "Reliability and strong work ethic",
      "Attention to detail",
      "Ability to work independently",
      "Good communication skills"
    ],
    featured: false,
    urgent: false,
  },
  {
    id: "cleaner-croydon-cr0-basic-dbs",
    title: "Cleaner (Basic DBS)",
    location: "Croydon, Greater London, CR0",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (less than 30 hours per week)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    closingDate: "2026-12-31",
    datePosted: "2026-09-29",
    postcodes: ["CR0"],
    description: "To provide reliable, ongoing part-time cleaning in Croydon (CR0), keeping offices, corridors, communal areas and washroom facilities hygienic, safe and well maintained.",
    responsibilities: [
      "General cleaning of offices, corridors and communal areas.",
      "Cleaning and restocking washroom facilities.",
      "Vacuuming, mopping, dusting and surface sanitising.",
      "Emptying waste bins and managing waste segregation.",
      "Following all site health and safety and COSHH procedures."
    ],
    qualifications: [
      "Previous cleaning experience preferred.",
      "Basic DBS check required (must be held before starting).",
      "Ability to work unsupervised and follow a cleaning schedule.",
      "Punctual, reliable and presentable.",
      "Right to work in the UK."
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (to bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all site health and safety procedures.",
      "Handle cleaning materials in line with COSHH guidance.",
      "Maintain safe working practices and report hazards immediately."
    ],
    competencies: [
      "Reliability and strong work ethic",
      "Attention to detail",
      "Ability to work independently",
      "Good communication skills"
    ],
    featured: false,
    urgent: false,
  },
  {
    id: "weekend-cleaner-horfield-bristol-bs7-basic-dbs",
    title: "Weekend Cleaner (Basic DBS)",
    location: "Horfield, Bristol, BS7",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Saturday and Sunday, 9:00 AM to 12:00 PM (6 hours per week)",
    duration: "10 October 2026 to 8 November 2026",
    dbsRequired: "Basic DBS",
    closingDate: "2026-11-08",
    datePosted: "2026-10-05",
    openings: 1,
    postcodes: ["BS7"],
    description: "To provide reliable weekend cleaning in Horfield, Bristol (BS7), from 10 October to 8 November 2026, keeping offices, corridors, communal areas and washroom facilities hygienic, safe and well maintained.",
    responsibilities: [
      "General cleaning of offices, corridors and communal areas.",
      "Cleaning and restocking washroom facilities.",
      "Vacuuming, mopping, dusting and surface sanitising.",
      "Emptying waste bins and managing waste segregation.",
      "Following all site health and safety and COSHH procedures."
    ],
    qualifications: [
      "Previous cleaning experience preferred.",
      "Basic DBS check required (must be held before starting).",
      "Ability to work unsupervised and follow a cleaning schedule.",
      "Punctual, reliable and presentable.",
      "Right to work in the UK."
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all site health and safety procedures.",
      "Handle cleaning materials in line with COSHH guidance.",
      "Maintain safe working practices and report hazards immediately."
    ],
    competencies: [
      "Reliability and strong work ethic",
      "Attention to detail",
      "Ability to work independently",
      "Good communication skills"
    ],
    featured: false,
    pinned: true,
    urgent: true,
    removeAfter: "2026-10-10",
  },
  {
    id: "cleaner-horfield-bristol-bs7-basic-dbs",
    title: "Cleaner (Basic DBS)",
    location: "Horfield, Bristol, BS7",
    payRate: "£12.71 per hour",
    contractType: "Temporary",
    category: "Commercial Cleaning",
    schedule: "Part-time (less than 30 hours per week)",
    duration: "Ongoing",
    dbsRequired: "Basic DBS",
    closingDate: "2026-12-31",
    datePosted: "2026-10-05",
    postcodes: ["BS7"],
    description: "To provide reliable, ongoing part-time cleaning in Horfield, Bristol (BS7), keeping offices, corridors, communal areas and washroom facilities hygienic, safe and well maintained.",
    responsibilities: [
      "General cleaning of offices, corridors and communal areas.",
      "Cleaning and restocking washroom facilities.",
      "Vacuuming, mopping, dusting and surface sanitising.",
      "Emptying waste bins and managing waste segregation.",
      "Following all site health and safety and COSHH procedures."
    ],
    qualifications: [
      "Previous cleaning experience preferred.",
      "Basic DBS check required (must be held before starting).",
      "Ability to work unsupervised and follow a cleaning schedule.",
      "Punctual, reliable and presentable.",
      "Right to work in the UK."
    ],
    uniform: [
      "Non-branded black t-shirt",
      "Dark coloured trousers",
      "Non-slip safety shoes",
      "Black warm jumper (bring in case it is cold on site)",
      "Tabard will be provided on site"
    ],
    healthSafety: [
      "Follow all site health and safety procedures.",
      "Handle cleaning materials in line with COSHH guidance.",
      "Maintain safe working practices and report hazards immediately."
    ],
    competencies: [
      "Reliability and strong work ethic",
      "Attention to detail",
      "Ability to work independently",
      "Good communication skills"
    ],
    featured: false,
    urgent: false,
  },
];


// Strip trailing UK outward postcode (with or without preceding comma)
// e.g. "Camborne, Cornwall, TR14" -> "Camborne, Cornwall"
// e.g. "Greenwich, London SE3"    -> "Greenwich, London"
export const stripPostcode = (location: string): string =>
  location.replace(/[,\s]+[A-Z]{1,2}\d[A-Z\d]?\s*$/i, "").trim();

export const isJobVisibleOn = (job: Pick<Job, "removeAfter">, today: Date): boolean => {
  if (!job.removeAfter) return true;
  const currentDate = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0"),
  ].join("-");
  return currentDate <= job.removeAfter;
};

export const isJobVisible = (job: Pick<Job, "removeAfter">): boolean =>
  isJobVisibleOn(job, new Date());

const visibleJobs = jobs.filter(isJobVisible);

export const locations = Array.from(new Set(visibleJobs.map(job => stripPostcode(job.location)))).sort();
export const categories = Array.from(new Set(visibleJobs.map(job => job.category))).sort();
export const contractTypes = Array.from(new Set(visibleJobs.map(job => job.contractType))).sort();

// Get all unique postcodes from jobs and the mapping
export const allPostcodes = Array.from(new Set([
  ...Object.keys(postcodeLocationMap),
  ...visibleJobs.flatMap(job => job.postcodes || [])
])).sort();

// Helper function to check if a search query matches postcodes
export const matchesPostcode = (job: Job, searchQuery: string): boolean => {
  const query = searchQuery.toUpperCase().trim();
  
  // Check if job has explicit postcodes
  if (job.postcodes?.some(pc => pc.toUpperCase().startsWith(query) || query.startsWith(pc.toUpperCase()))) {
    return true;
  }
  
  // Check if search matches a postcode that maps to this job's location
  for (const [postcode, locations] of Object.entries(postcodeLocationMap)) {
    if (postcode.toUpperCase().startsWith(query) || query.startsWith(postcode.toUpperCase())) {
      if (locations.some(loc => job.location.toLowerCase().includes(loc.toLowerCase()))) {
        return true;
      }
    }
  }
  
  return false;
};
